import { useState } from "react";
import { useParams } from "react-router-dom";
import {
  Card,
  Stack,
  Row,
  Col,
  Form,
  Button,
  Alert,
} from "react-bootstrap";
import { createSupportTicketRequest } from "@/services";

const LMS_SUPPORT_SUBJECT_OPTIONS = [
  "Issue with Business KYC",
  "Issue with Individual KYC",
  "Shareholder / Partner Details Issue",
  "Seller / Platform Connection Issue",
  "Document Upload or Verification Issue",
  "Credit Evaluation Issue",
  "Agreement / E-Sign Issue",
  "Bank Account / Settlement Issue",
  "Post-Sanction Query",
  "Active Loan / Repayment Query",
  "Other",
];

export function CreateSupportTicket({ onSuccess }) {
  const { loanUUID: loanId } = useParams();

  const [subject, setSubject] = useState("");
  const [customSubject, setCustomSubject] = useState("");
  const [message, setMessage] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setAttachments((prev) => [...prev, ...files]);
    e.target.value = null;
  };

  const removeFile = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const finalSubject =
      subject === "Other" ? customSubject : subject;

    if (!finalSubject || !message) return;

    const formData = new FormData();
    formData.append("loan", loanId);
    formData.append("subject", finalSubject);
    formData.append("description", message);

    attachments.forEach((file) =>
      formData.append("attachments", file)
    );

    try {
      setSubmitting(true);
      setError(null);
      const res = await createSupportTicketRequest(formData);

      if (res?.success === false) {
        setError(
          typeof res.error === "string" ? res.error : "Failed to submit ticket"
        );
        return;
      }

      onSuccess?.();

      setSubject("");
      setCustomSubject("");
      setMessage("");
      setAttachments([]);
    } catch (err) {
      setError(err?.message || "Failed to submit ticket");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="border-0 rounded-lg">
      <Card.Body>
        <Form onSubmit={handleSubmit}>
          <Stack gap={4}>
            <Row className="g-4">
              <Col md={6}>
                <Form.Label>Subject</Form.Label>
                <Form.Select
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  required
                >
                  <option value="">Select subject</option>
                  {LMS_SUPPORT_SUBJECT_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              {subject === "Other" && (
                <Col xs={12}>
                  <Form.Label>Custom subject</Form.Label>
                  <Form.Control
                    value={customSubject}
                    onChange={(e) => setCustomSubject(e.target.value)}
                    placeholder="Enter your subject"
                    required
                  />
                </Col>
              )}

              <Col xs={12}>
                <Form.Label>Your message</Form.Label>
                <Form.Control
                  as="textarea"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  style={{ minHeight: 220 }}
                />
              </Col>

              <Col xs={12}>
                <Form.Label>Attachments (optional)</Form.Label>
                <Form.Control type="file" multiple onChange={handleFileChange} />
              </Col>
            </Row>

            {attachments.length > 0 && (
              <Stack gap={2}>
                {attachments.map((file, index) => (
                  <div
                    key={index}
                    className="d-flex justify-content-between align-items-center border rounded p-2"
                  >
                    <span>{file.name}</span>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => removeFile(index)}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </Stack>
            )}

            {error && <Alert variant="danger">{error}</Alert>}

            <Button
              type="submit"
              className="bg-black border-0 px-4 py-2"
              disabled={submitting}
            >
              {submitting ? "Submitting..." : "Submit ticket"}
            </Button>
          </Stack>
        </Form>
      </Card.Body>
    </Card>
  );
}
